import { useEffect, useState } from "react";
import { Receipt, ChevronDown, Loader2 } from "lucide-react";
import { fetchInvoices } from "@/lib/api";

const fmt = (n) => `$${Number(n || 0).toFixed(2)}`;
const STATUS_TONE = {
  paid: "text-emerald-700 bg-emerald-50",
  cancelled: "text-rose-600 bg-rose-50",
  processing: "text-sky-700 bg-sky-50",
};

// Admin profile sheet: the dealer's invoice ledger (sales made outside the web
// shop). Read-only; totals exclude cancelled invoices.
export default function AdminInvoicesSection({ userRecordId, callerUserRecordId, open }) {
  const [expanded, setExpanded] = useState(true);
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState(null);

  useEffect(() => {
    if (!(open && userRecordId)) return;
    let active = true;
    setLoading(true);
    fetchInvoices(userRecordId, { callerUserRecordId })
      .then((res) => active && setData(res))
      .catch(() => active && setData(null))
      .finally(() => active && setLoading(false));
    return () => {
      active = false;
    };
  }, [open, userRecordId, callerUserRecordId]);

  const invoices = data?.invoices || [];

  return (
    <div className="rounded-2xl border border-zinc-200 overflow-hidden" data-testid="admin-edit-invoices">
      <button
        type="button"
        onClick={() => setExpanded((v) => !v)}
        className="w-full flex items-center gap-2 px-4 py-3 text-left"
        data-testid="admin-edit-invoices-toggle"
      >
        <Receipt size={15} className="text-violet-600" />
        <span className="flex-1 text-sm font-semibold text-zinc-900">Invoices</span>
        {!loading && data ? (
          <span className="text-[11px] font-semibold text-violet-700 bg-violet-50 rounded-full px-2 py-0.5" data-testid="admin-edit-invoices-total">
            {data.count} · {fmt(data.total_value)}
          </span>
        ) : (
          <span className="text-[11px] font-semibold text-violet-700 bg-violet-50 rounded-full px-2 py-0.5">…</span>
        )}
        <ChevronDown size={14} className={`text-zinc-400 transition-transform ${expanded ? "rotate-180" : ""}`} />
      </button>
      {expanded && (
        <div className="border-t border-zinc-100 bg-white">
          {loading ? (
            <div className="py-6 text-center text-zinc-500 text-xs">
              <Loader2 size={13} className="animate-spin inline mr-1" /> Loading invoices…
            </div>
          ) : invoices.length === 0 ? (
            <div className="py-6 text-center text-zinc-500 text-xs">No invoices yet.</div>
          ) : (
            <ul className="divide-y divide-zinc-100 max-h-[260px] overflow-y-auto" data-testid="admin-edit-invoices-list">
              {invoices.map((inv) => {
                const st = String(inv.status || "pending").toLowerCase();
                return (
                  <li key={inv.id} className="flex items-center gap-2 px-4 py-2.5" data-testid={`admin-edit-invoice-${inv.id}`}>
                    <div className="flex-1 min-w-0">
                      <div className="text-sm font-medium text-zinc-900 truncate">
                        #{inv.order_id}
                        <span className="ml-1.5 text-zinc-500 font-normal">{inv.name}</span>
                      </div>
                      <div className="text-[11px] text-zinc-500 flex items-center gap-1.5">
                        <span>{inv.date}</span>
                        {inv.payment_method ? <span>· {inv.payment_method}</span> : null}
                        {inv.discount_amount ? <span className="text-emerald-700">· −{fmt(inv.discount_amount)} disc</span> : null}
                      </div>
                    </div>
                    <span className={`text-[9px] uppercase tracking-wide font-semibold rounded-full px-1.5 py-0.5 ${STATUS_TONE[st] || "text-amber-700 bg-amber-50"}`}>
                      {st}
                    </span>
                    <div className={`text-sm font-semibold shrink-0 ${inv.cancelled ? "text-rose-600 line-through" : "text-zinc-900"}`}>
                      {fmt(inv.cancelled ? inv.original_total : inv.total)}
                    </div>
                  </li>
                );
              })}
            </ul>
          )}
        </div>
      )}
    </div>
  );
}
